import * as yup from "yup";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import { languages, levelValue, levels } from "config";
import {
  useAddNewTechSkillsMutation,
  useDeleteCvTechSkillsMutation,
  useEditCvTechSkillsMutation,
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import { yupResolver } from "@hookform/resolvers/yup";

const cvSchema = yup.object().shape({
  language: yup.string().required("Language is required"),
  level: yup.string().required("Level is required"),
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const renderDots = (level, maxCount) => {
  const dots = [];
  for (let i = 0; i < maxCount; i++) {
    dots.push(
      <div
        key={i}
        sx={{
          height: 16,
          width: 16,
          borderRadius: "50%",
          marginRight: 0.5,
          backgroundColor: i < level ? "#2196f3" : "#9e9e9e",
        }}
      />
    );
  }
  return dots;
};

const TechSkills = ({ techSkills, cvId }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [techSkillsState, setTechSkillsState] = useState(techSkills);
  const [techSkillsId, setSkillId] = useState();
  const [techElmt, setTechElement] = useState(null);
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvSchema),
    defaultValues: {
      language: "",
      level: "",
      excludeInCv: false,
    },
  });

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    techSkills.forEach((skills) => {
      const savedState = localStorage.getItem(`excludeInCv-${skills._id}`);
      initialLocalExcludeInCv[skills._id] = savedState === "true";
    });

    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [techSkills]);

  const [addNewTechSkills] = useAddNewTechSkillsMutation();

  const submitResults = async (e) => {
    const newTechSkill = e;
    await addNewTechSkills({ cvId, techSkills: [newTechSkill] }).unwrap();
    handleClose();
    techSkills.push(newTechSkill); // add the new skill to the existing array
    toast.success("Programming Skill(s) added successfully");
  };

  const [deleteCvTechSkills] = useDeleteCvTechSkillsMutation();

  const handleRemove = async (item) => {
    await deleteCvTechSkills({ cvId, techSkillsId: item }).unwrap();
    // Create a new array that excludes the deleted item
    const updatedTechSkills = techSkillsState.filter(
      (skill) => skill.id !== item
    );
    // Update the state with the new array
    setTechSkillsState(updatedTechSkills);
    // Update the techSkills prop passed to the component
    techSkills.splice(
      techSkills.findIndex((skill) => skill._id === item),
      1
    );
    setTechSkillsState([...techSkills]);

    toast.info("Programming Skill(s) removed successfully");
  };

  const [editCvTechSkills] = useEditCvTechSkillsMutation();
  const handleEdit = (techSkillsId, mode) => {
    setOpen(true);
    setMode(mode);
    setSkillId(techSkillsId);
  };

  const handleInputChange = (event, techSkillsId, fieldName) => {
    const techSkillElement = techSkills.find(
      (skills) => skills._id === techSkillsId
    );
    techSkillElement[fieldName] = event.target.value;

    const updatedTechSkill = techSkills.map((skills) => {
      if (skills._id === techSkillsId) {
        return { ...skills, [fieldName]: event.target.value };
      }
      return skills;
    });
    setTechSkillsState(updatedTechSkill);
  };

  const handleToggleChange = async (
    newExcludeInCv,
    techSkillsId,
    fieldName,
    updatedValue
  ) => {
    try {
      const updatedCerts = techSkills.map((skills) => {
        if (skills._id === techSkillsId) {
          return {
            ...skills,
            [fieldName]: updatedValue,
          };
        }
        return skills;
      });

      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [techSkillsId]: newExcludeInCv,
      }));

      localStorage.setItem(
        `excludeInCv-${techSkillsId}`,
        newExcludeInCv.toString()
      );

      await editCvTechSkills({
        cvId: cvId,
        techSkillsId: techSkillsId,
        techSkills: updatedCerts.map((skills) => ({
          ...skills,
          excludeInCv: newExcludeInCv,
        })),
      });

      setTechSkillsState(updatedCerts);
    } catch (error) {
      console.error("Error updating toggle:", error);
    }
  };

  const techSkillElement = techSkills.find(
    (skills) => skills._id === techSkillsId
  );

  const renderSingleElement = () => {
    return (
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: "10px" }}
      >
        <Grid item xs={12}>
          <Controller
            name="language"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Language"
                select
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.language}
                helperText={errors?.language ? errors.language?.message : ""}
                value={techSkillElement.language}
                onChange={(e) => handleInputChange(e, techSkillsId, "language")}
              >
                {languages.map((language) => (
                  <MenuItem value={language.name} key={language.name}>
                    {language.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="level"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Level"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                value={techSkillElement.level}
                onChange={(e) => handleInputChange(e, techSkillsId, "level")}
                error={!!errors.level}
                helperText={errors?.level ? errors.level?.message : ""}
              >
                {levels.map((level) => (
                  <MenuItem value={level.value} key={level.value}>
                    {level.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const submitEditResults = async () => {
    await editCvTechSkills({
      cvId,
      techSkillsId,
      techSkills: [techSkillElement],
    }).unwrap();
    handleClose();
    toast.info("Programming Skills updated successfully");
  };

  return (
    <div id="techSkills">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper style={{ marginTop: "20px", padding: "15px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              style={{ marginTop: "5px" }}
            >
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 0, pb: 0 }}
                    >
                      Programming Skills &nbsp;
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title="This section is mandatory"
                      >
                        <span style={{ color: "red" }}>*</span>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {techSkills.map((language) => (
                  <List key={language._id} style={{ paddingLeft: "10px" }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: cyan[700] }}>
                          <LanguageIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        style={{ paddingLeft: "10px" }}
                        primary={
                          <Grid
                            container
                            alignItems="flex-start"
                            xs={12}
                            key={language._id}
                            display="flex"
                            mb={1}
                          >
                            <Grid item xs={2}>
                              <Typography variant="subtitle2" components="div">
                                {language.language}
                              </Typography>
                            </Grid>
                            <Grid item display="column" xs={3}>
                              <div sx={{ display: "flex", alignItems: "center" }}>
                                {renderDots(Number(language.level), 5)}
                              </div>
                              <Typography variant="subtitle2" components="div">
                                {levelValue[Number(language.level) - 1].name}
                                <Typography variant="caption" display="block">
                                  Exclude in cv:{" "}
                                  {language.excludeInCv ? "Yes" : "No"}
                                </Typography>
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        style={{ textAlign: "right", paddingRight: "40px" }}
                      >
                        <FormControl>
                          <FormHelperText error={!!errors.excludeInCv}>
                            {errors?.excludeInCv
                              ? errors.excludeInCv?.message
                              : ""}
                          </FormHelperText>
                          <Controller
                            key={language._id}
                            name={`excludeInCv-${language._id}`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                            }) => (
                              <Tooltip title="This will exclude the section from the CV">
                                <FormControlLabel
                                  style={{ margin: "0px" }}
                                  control={
                                    <Switch
                                      onChange={(e) => {
                                        const newExcludeInCv =
                                          e.target.checked;
                                        
                                        setLocalExcludeInCv(
                                          (prevLocalExcludeInCv) => ({
                                            ...prevLocalExcludeInCv,
                                            [language._id]: newExcludeInCv,
                                          })
                                        );

                                        handleToggleChange(
                                          newExcludeInCv,
                                          language._id,
                                          "excludeInCv"
                                        );

                                        console.log(
                                          "Toggle updated successfully"
                                        );
                                      }}
                                      checked={
                                        localExcludeInCv[language._id] || false
                                      }
                                      {...field}
                                      ref={null}
                                    />
                                  }
                                />
                              </Tooltip>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <DialogTitle>Add a New Programming Skill </DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "10px" }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="language"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Language"
                        select
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.language}
                        helperText={
                          errors?.language ? errors.language?.message : ""
                        }
                      >
                        {languages.map((language) => (
                          <MenuItem value={language.name} key={language.name}>
                            {language.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="level"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Level"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        value={field.value}
                        error={!!errors.level}
                        helperText={errors?.level ? errors.level?.message : ""}
                      >
                        {levels.map((level) => (
                          <MenuItem value={level.value} key={level.value}>
                            {level.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Programming Skill</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "5px" }}
              >
                <Grid item xs={12}>
                  {techSkills?.map((skill) => (
                    <List key={skill._id}>
                      <ListItem
                        style={{ marginRight: "-2rem" }}
                        secondaryAction={
                          <Stack alignItems="left" direction="row">
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(skill._id, "editnew")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(skill._id)}
                            >
                              <DeleteIcon style={{ color: red[400] }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          style={{ paddingRight: "20px", margin: "0px" }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              {skill.language}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" display="block">
                              {levelValue[Number(skill.level) - 1].name}
                              <Typography variant="caption" display="block">
                                Exclude in cv:{" "}
                                {skill.excludeInCv ? "Yes" : "No"}
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit Programming Skills</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default TechSkills;